/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



import { Grid, Stack } from "@mui/material";
import { visitOfficeListOptions } from "components/forms/custom/SelectOptionsList";
import Box from "components/generals/Box";
import FormSelectInput from "components/generals/FormSelectInput";
import Typography from "components/generals/Typography";
import FormikForm from "components/layout/FormikForm";
import { Form } from "formik";
import { AppointmentInterface } from "medicalsystem-shared";
import { FC } from "react";
import Button from "components/generals/Button";
import { officeVisitFormConfig } from "utils/form.utils";
import FormField from "components/generals/FormInput";
import RateReviewIcon from "@mui/icons-material/RateReview";
import { useTranslation } from "react-i18next";

/**
 * Properties for the NewOfficeVisitForm component.
 */
export interface OfficeVisitFormProps {
  model: AppointmentInterface;
}

/**
 * Office visit form component for recording visit details during patient check-in.
 * 
 * Features:
 * - Arrival time input (time picker)
 * - Visit duration in minutes
 * - Room number assignment
 * - Visit office status selection (checked-in, in-progress, completed, etc.)
 * - Form validation with officeVisitFormConfig
 * - Responsive grid layout
 * - Icon-based header with RateReview icon
 * 
 * Use Cases:
 * - Front desk check-in workflow
 * - Recording patient arrival details
 * - Room assignment during check-in
 * - Tracking visit status progression
 * 
 * Note: Contains TODO for HTTP client integration.
 * Currently logs data to console instead of saving to backend.
 * 
 * @param {OfficeVisitFormProps} props - Component properties
 * @param {AppointmentInterface} props.model - Appointment data with visit details
 * 
 * @returns {JSX.Element} Office visit form with arrival time and room assignment
 */
const NewOfficeVisitForm: FC<OfficeVisitFormProps> = ({ model }) => {
  //TODO: HTTP client to store patient data
  const { t } = useTranslation();
  const submitHandle = (patient: AppointmentInterface) => {
    console.log(JSON.stringify(patient));
  };

  return (
    <FormikForm
      onSubmit={(model) => {
        submitHandle(model);
      }}
      initialValues={model}
      validationSchema={officeVisitFormConfig}
    >
      {({ values, errors, touched, isSubmitting }) => (
        <Form id="officeVisitData" autoComplete="off">
          <Box className="form-container" p={3} mb={4}>
            <Stack alignItems="center" direction="row" gap={2}>
              <RateReviewIcon fontSize="large" />
              <Typography variant="h3">{t("newOfficeVisitForm.officeVisit")}</Typography>
            </Stack>
            <br />
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <Typography variant="h5">{t("newOfficeVisitForm.arrivalTime")}</Typography>
                <FormField
                  type="time"
                  name="arrivalDateTime"
                  fullWidth
                  value={values.arrivalDateTime}
                  error={
                    errors.arrivalDateTime !== undefined && touched.arrivalDateTime !== undefined
                  }
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="h5">{t("newOfficeVisitForm.duration")} (min)</Typography>
                <FormField
                  type="number"
                  name="duration"
                  fullWidth
                  value={values.duration}
                  error={errors.duration && touched.duration}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <FormField
                  type="text"
                  name="roomNumber"
                  label={t("newOfficeVisitForm.roomNumber")}
                  fullWidth
                  value={values.roomNumber}
                  error={errors.roomNumber && touched.roomNumber}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box>
                  <FormSelectInput
                    name="visitOfficeStatus"
                    label={t("newOfficeVisitForm.visitOfficeStatus")}
                    selectArray={visitOfficeListOptions}
                    defaultValue={values.visitOfficeStatus}
                  />
                </Box>
              </Grid>
            </Grid>
            <Box mt={2} width="100%" display="flex" justifyContent="end">
              <Grid item>
                <Button variant="contained" color="secondary">
                  {t("newOfficeVisitForm.cancel")}
                </Button>
              </Grid>
              <Grid item>
                <Button type="submit" disabled={isSubmitting} variant="contained" color="info">
                  {t("newOfficeVisitForm.save")}
                </Button>
              </Grid>
            </Box>
          </Box>
        </Form>
      )}
    </FormikForm>
  );
};

export default NewOfficeVisitForm;
